import React from 'react';
import StatusBadge from '../StatusBadge';

// The pieces of one timesheet day, kept out of approvalQueue so the attendance
// correction modal can render them without pulling the whole approvals queue in
// with it. Both popups show the same day, so they share one implementation.

export const KIND_LABEL = {
  add_in: 'Add missing time in',
  add_out: 'Add missing time out',
  fix: 'Change a time',
  overtime: 'Add extra hours',
  mark_home: 'Mark as from home',
  mark_office: 'Mark as at office',
};

export const formatTime = (iso, timeZone) => {
  if (!iso) return '-';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', timeZone });
};

export const formatMinutes = (minutes) => {
  if (minutes == null) return '-';
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const snapshot = (times, timeZone) => {
  if (!times) return '-';
  const span = `${formatTime(times.in_at, timeZone)} → ${formatTime(times.out_at, timeZone)}`;
  return `${span} · ${formatMinutes(times.worked_minutes)}`;
};

/** The day's effective totals: when it started and ended, and against what. */
export const DayTotals = ({ day, timeZone }) => (
  <div
    className="grid grid-cols-1 gap-2 rounded-md bg-gray-50 p-3 sm:grid-cols-[2fr_1fr_1fr]"
    data-testid="day-totals"
  >
    <div>
      <p className="text-xs text-gray-500">In → Out</p>
      <p className="whitespace-nowrap font-medium">
        {formatTime(day.in_at, timeZone)} → {formatTime(day.out_at, timeZone)}
      </p>
    </div>
    <div>
      <p className="text-xs text-gray-500">Worked</p>
      <p className="font-medium">{formatMinutes(day.worked_minutes)}</p>
    </div>
    <div>
      <p className="text-xs text-gray-500">Expected</p>
      <p className="font-medium">{formatMinutes(day.expected_minutes)}</p>
    </div>
  </div>
);

/** One day's punch timeline with its source tags. */
export const DayTimeline = ({ events, timeZone }) => (
  <div>
    <p className="mb-1 font-medium text-gray-700">Timeline</p>
    {events?.length ? (
      <ul className="divide-y divide-gray-100 rounded-md border border-gray-200" data-testid="day-events">
        {events.map((event) => (
          <li
            key={event.id}
            className={`flex items-baseline gap-3 px-3 py-2 ${
              event.voided ? 'text-gray-400 line-through' : ''}`}
          >
            <span className="w-16 shrink-0 text-xs text-gray-500">{formatTime(event.at, timeZone)}</span>
            <span className="flex-1">
              {event.label}
              {event.note && <span className="block text-xs text-gray-500 no-underline">{event.note}</span>}
            </span>
            <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600">{event.source}</span>
          </li>
        ))}
      </ul>
    ) : <p className="text-gray-400">No punches</p>}
  </div>
);

/** Every amendment filed against the day, with what it changed. */
export const DayAmendments = ({ amendments, timeZone }) => (
  <div>
    <p className="mb-1 font-medium text-gray-700">Amendments</p>
    {amendments?.length ? (
      <ul className="space-y-2" data-testid="day-amendments">
        {amendments.map((amendment) => (
          <li key={amendment.id} className="rounded-md border border-gray-200 px-3 py-2">
            <div className="flex items-center justify-between gap-2">
              <span className="font-medium">{KIND_LABEL[amendment.kind] || amendment.kind}</span>
              <StatusBadge status={amendment.status} type="amendment" />
            </div>
            <p className="text-gray-600">“{amendment.reason}”</p>
            <p className="text-xs text-gray-500">
              {snapshot(amendment.before, timeZone)} → {snapshot(amendment.after, timeZone)}
            </p>
          </li>
        ))}
      </ul>
    ) : <p className="text-gray-400">None</p>}
  </div>
);
